import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { paths } from 'routes/paths';
import { DUMMY_SEASON, FETCH_TYPES } from 'helpers/constants';
import useSelectedCropSeasonIds from 'hooks/useSelectedCropSeasonIds';
import featureFlagRenderItem from 'helpers/featureFlagRenderItem';
import { getCropZonesFromProperties } from 'helpers/propertyHelpers';
import { getLocalStorageApi, STORAGE_KEYS } from 'utilities/localStorage';
import { AmplitudeContext } from 'utilities/amplitude/useAmplitude';
import usePopulateMapData from 'hooks/usePopulateMapData';
import { Context } from 'components/Store';
import createAction from 'helpers/createAction';
import {
  SELECT_PROPERTY,
  SELECTED_FIELD_ROW_ID,
  SET_PROPERTY_FILTERS,
  SEARCH_TEXT_VALUE_FFT
} from 'reducers/reducer';
import { ROLES } from 'utilities/access';
import {
  PROPERTY_LANDING_MAP_CORDS,
  PROPERTY_CREATE_STATE,
  PROPERTY_VIEW_MODE
} from 'screens/Property/helpers/constants';
import { getStringParsed } from 'utilities/helpers';
import shouldResetViewMode from './helpers/shouldResetViewMode';
import filterFeatureCollectionByParentIdNoZoom from './helpers/filterFeatureCollectionByParentIdNoZoom';
import {
  defaultCropZoneFilters,
  getCropZoneFilters,
  filterLandingMap,
  getLandingPropertyResults,
  hasCropZoneFilters,
  getMapFilterSignature,
  pruneUnavailableCropZoneSelections
} from './helpers/propertyFilters';

const PROPERTY_FILTER_SCOPE_KEY = 'propertyFiltersScope';
const initialState = {};
const PropertyLandingContext = createContext(initialState);

export default PropertyLandingContext;

const propertyViewStorageApi = getLocalStorageApi(
  STORAGE_KEYS.PROPERTY_VIEW_V2,
  PROPERTY_VIEW_MODE.MAP
);

export function PropertyLandingProvider({ children }) {
  const amplitude = useContext(AmplitudeContext);
  const history = useHistory();
  const flags = useFlags();

  const [
    {
      organization,
      selectedProperty,
      loggedInUserOrgPermission,
      searchTextValueFFT,
      propertyFilters,
      tagCatalogue,
      isMassAssignerActive,
      isExportPDFMapsActive,
      isGlobalCropSeasonLoading
    },
    dispatch
  ] = useContext(Context);
  const activeTagCatalogue =
    tagCatalogue && tagCatalogue.organizationId === organization?.id
      ? tagCatalogue.tags
      : undefined;

  const selectedPropertyRef = useRef(selectedProperty);
  selectedPropertyRef.current = selectedProperty;

  const [isFormDirty, setFormDirty] = useState(false);
  const [isNavigationBlocked, setNavigationBlocked] = useState(false);
  // The editor is scoped to whichever property is open, so it lives beside the
  // guard: every route that leaves the form has to close it, or the property we
  // land on opens straight into edit mode.
  const [enableEdit, setEnableEdit] = useState(false);

  // Both are read through refs so the guard can wrap callbacks that
  // deliberately keep empty dependency lists. The deferred navigation is held
  // in a ref rather than state so it is never invoked from inside a state
  // updater, which React may run more than once.
  const isFormDirtyRef = useRef(isFormDirty);
  isFormDirtyRef.current = isFormDirty;
  const pendingNavigationsRef = useRef([]);
  const pendingCancelActionsRef = useRef([]);

  const guardNavigation = useCallback((proceed, onCancel) => {
    if (isFormDirtyRef.current) {
      // One gesture can ask to navigate more than once: the tree's edit button
      // selects its row, pushes the row's route, and then the row's own click
      // handler selects it again as the event bubbles. The prompt blocks the
      // page meanwhile, so everything queued here belongs to that one gesture
      // and is replayed in order - keeping only the last would silently drop
      // the route push and leave the user where they started.
      if (proceed) {
        pendingNavigationsRef.current.push(proceed);
      }
      // Optional: undo a side effect the caller couldn't avoid running before
      // this guard got a chance to block it (e.g. a dropdown that persists its
      // own selection ahead of calling back) if the user discards the switch.
      if (onCancel) {
        pendingCancelActionsRef.current.push(onCancel);
      }
      setNavigationBlocked(true);
      return;
    }
    setEnableEdit(false);
    proceed?.();
  }, []);

  const confirmNavigation = useCallback(() => {
    const pending = pendingNavigationsRef.current;
    pendingNavigationsRef.current = [];
    pendingCancelActionsRef.current = [];
    setNavigationBlocked(false);
    // A queued step can go back through the guard - switching the view is
    // itself guarded, and the bulk actions switch it - and the state update
    // below has not rendered by then. Clearing the ref the guard actually
    // reads is what stops the prompt coming straight back up.
    isFormDirtyRef.current = false;
    setFormDirty(false);
    setEnableEdit(false);
    pending.forEach(proceed => proceed());
  }, []);

  const cancelNavigation = useCallback(() => {
    const pendingCancelActions = pendingCancelActionsRef.current;
    pendingNavigationsRef.current = [];
    pendingCancelActionsRef.current = [];
    setNavigationBlocked(false);
    pendingCancelActions.forEach(onCancel => onCancel());
  }, []);

  const { propertyCount } = organization?.properties || {};
  const isHugeOrganization =
    flags.configureLargeGrowerConditionals?.propertiesThreshold &&
    propertyCount >= flags.configureLargeGrowerConditionals.propertiesThreshold;

  const [params, setParams] = useState({ page: 0, includeArchived: true });
  const [selectedField, setSelectedField] = useState();
  const [financialAccess, setFinancialAccess] = useState();
  const [viewMode, setViewMode] = useState(propertyViewStorageApi.get());
  const { selectedCropSeasonIds } = useSelectedCropSeasonIds();
  const resetPropertyFiltersForOrganizationChange = useCallback(() => {
    dispatch({
      type: SET_PROPERTY_FILTERS,
      payload: {
        showFarmWithIds: [],
        showArchivedProperties: false,
        cropZoneFilters: { ...defaultCropZoneFilters }
      }
    });
    dispatch({ type: SEARCH_TEXT_VALUE_FFT, payload: { valueInput: '' } });
  }, [dispatch]);

  const {
    loading: isLoadingProperties,
    zoom: propertyZoom,
    setZoom: onPropertySetZoom,
    dataSorted: propertyLandingPageData,
    reloadData,
    fieldsAndAreasGeoJSONCollection: unfilteredGeoJSON,
    setGeoJSONCollectionFeatures,
    updateCropZoneTags: updateLoadedCropZoneTags,
    fieldsLoading: isLoadingFields,
    filterFeatureCollectionByParentId,
    fetchFarmCosts,
    isLoadingPaginatedProperties,
    pagination,
    successfulPropertiesLoadId,
    lastSuccessfulSeasonIds,
    lastSuccessfulProperties
  } = usePopulateMapData({
    fetchType: isHugeOrganization
      ? FETCH_TYPES.paginatedPropertiesMap
      : FETCH_TYPES.propertiesMap,
    isCacheOn: false
  });

  const pendingCropZoneTagUpdateRef = useRef(null);
  const updateCropZoneTags = (cropZoneId, tags) => {
    pendingCropZoneTagUpdateRef.current = cropZoneId;
    updateLoadedCropZoneTags(cropZoneId, tags);
  };

  useEffect(() => {
    const updatedCropZoneId = pendingCropZoneTagUpdateRef.current;
    if (!updatedCropZoneId) return;
    pendingCropZoneTagUpdateRef.current = null;

    const properties = propertyLandingPageData?.properties ?? [];
    const hasUpdatedCropZone = getCropZonesFromProperties(properties).some(
      zone => zone?.id === updatedCropZoneId
    );
    if (!hasUpdatedCropZone) return;

    const previous = getCropZoneFilters(propertyFilters?.cropZoneFilters);
    const { tagIds } = pruneUnavailableCropZoneSelections(
      properties,
      propertyFilters,
      activeTagCatalogue
    );
    if (tagIds.length !== previous.tagIds.length) {
      dispatch({
        type: SET_PROPERTY_FILTERS,
        payload: { cropZoneFilters: { ...previous, tagIds } }
      });
    }
  }, [propertyLandingPageData, propertyFilters, activeTagCatalogue, dispatch]);

  const lastReconciledPropertiesLoadIdRef = useRef(0);

  useEffect(() => {
    if (
      isLoadingProperties ||
      !successfulPropertiesLoadId ||
      successfulPropertiesLoadId === lastReconciledPropertiesLoadIdRef.current
    )
      return;
    const expectedSeasonIds = selectedCropSeasonIds.length
      ? [...selectedCropSeasonIds].sort((a, b) => a.localeCompare(b))
      : [DUMMY_SEASON];
    const loadedSeasonIds = [...(lastSuccessfulSeasonIds ?? [])].sort((a, b) =>
      a.localeCompare(b)
    );
    if (
      expectedSeasonIds.length !== loadedSeasonIds.length ||
      !expectedSeasonIds.every((id, index) => id === loadedSeasonIds[index])
    )
      return;

    lastReconciledPropertiesLoadIdRef.current = successfulPropertiesLoadId;
    let previousScope;
    try {
      previousScope = JSON.parse(
        localStorage.getItem(PROPERTY_FILTER_SCOPE_KEY) ?? 'null'
      );
    } catch {
      previousScope = null;
    }
    const organizationChanged =
      !!previousScope?.organizationId &&
      previousScope.organizationId !== organization?.id;
    if (organizationChanged) {
      resetPropertyFiltersForOrganizationChange();
    } else if (
      previousScope?.seasonIds &&
      JSON.stringify(previousScope.seasonIds) !==
        JSON.stringify(expectedSeasonIds)
    ) {
      const cropZoneFilters = pruneUnavailableCropZoneSelections(
        lastSuccessfulProperties ?? [],
        propertyFilters,
        activeTagCatalogue
      );
      const previous = getCropZoneFilters(propertyFilters?.cropZoneFilters);
      const same = (nextIds, oldIds) =>
        nextIds.length === oldIds.length &&
        nextIds.every((id, index) => id === oldIds[index]);
      if (
        !same(cropZoneFilters.cropIds, previous.cropIds) ||
        !same(cropZoneFilters.tagIds, previous.tagIds)
      ) {
        dispatch({
          type: SET_PROPERTY_FILTERS,
          payload: { cropZoneFilters }
        });
      }
    }
    try {
      localStorage.setItem(
        PROPERTY_FILTER_SCOPE_KEY,
        JSON.stringify({
          organizationId: organization?.id,
          seasonIds: expectedSeasonIds
        })
      );
    } catch {
      // Filtering remains functional for this session if storage is unavailable.
    }
  }, [
    isLoadingProperties,
    successfulPropertiesLoadId,
    lastSuccessfulSeasonIds,
    lastSuccessfulProperties,
    selectedCropSeasonIds,
    organization?.id,
    propertyFilters,
    activeTagCatalogue,
    dispatch,
    resetPropertyFiltersForOrganizationChange
  ]);

  const searchValue = searchTextValueFFT?.valueInput || '';
  const landingResults = getLandingPropertyResults(
    propertyLandingPageData?.properties,
    propertyFilters,
    searchValue,
    isHugeOrganization,
    activeTagCatalogue
  );
  const hasSearchFilter = !isHugeOrganization && !!searchValue.trim();
  const isSelectionFlow = isMassAssignerActive || isExportPDFMapsActive;
  const landingGeoJSON =
    !isSelectionFlow &&
    (propertyFilters?.showFarmWithIds?.length ||
      hasCropZoneFilters(propertyFilters?.cropZoneFilters) ||
      hasSearchFilter)
      ? filterLandingMap(
          unfilteredGeoJSON,
          propertyLandingPageData?.properties ?? [],
          landingResults.searchResults,
          propertyFilters?.showArchivedProperties
        )
      : unfilteredGeoJSON;
  const filterSignature = getMapFilterSignature(propertyFilters);
  const fieldsAndAreasGeoJSONCollection = landingGeoJSON?.features
    ? {
        ...landingGeoJSON,
        features: landingGeoJSON.features.map(feature =>
          feature.properties?.$filterSignature === filterSignature
            ? feature
            : {
                ...feature,
                properties: {
                  ...feature.properties,
                  $filterSignature: filterSignature
                }
              }
        )
      }
    : landingGeoJSON;
  const fieldsAndAreasGeoJSONRef = useRef(unfilteredGeoJSON);
  fieldsAndAreasGeoJSONRef.current = unfilteredGeoJSON;

  const isCostAnalysisViewEnabled = featureFlagRenderItem(
    flags.releaseCostAnalysisViewEnable,
    flags.releaseCostAnalysisViewEnableByOrg,
    organization.id
  );

  const isPropertyDetailsEnabled = featureFlagRenderItem(
    flags.fePropertyDetails,
    flags.fePropertyDetailsByOrgId,
    organization?.id
  );

  const onViewModeChange = useCallback(
    newView => {
      guardNavigation(() => {
        setViewMode(newView);
        propertyViewStorageApi.save(newView);
      });
    },
    [guardNavigation]
  );

  const onDrawFieldsClick = useCallback(
    (currentSelectedProperty, filteredProperties) => {
      amplitude.sendEventToAmplitude(
        amplitude.events.epic.Properties.createField
      );
      let selectedId = null;
      let parentFarm = null;

      if (currentSelectedProperty?.type === 'Farm') {
        selectedId = currentSelectedProperty.id;
        // Find the farm in filteredProperties to get complete data including fields
        parentFarm = filteredProperties?.find(
          property => property.id === currentSelectedProperty.id
        );
      } else if (currentSelectedProperty?.type === 'Field') {
        const filterField = filteredProperties
          ?.filter(property =>
            property?.fields?.find(
              field => field.id === currentSelectedProperty.id
            )
          )
          .pop();
        selectedId = filterField?.id;
        parentFarm = filterField;
      }

      const navigationState = {
        selectedId,
        mapCoordinates: getStringParsed(
          localStorage.getItem(PROPERTY_LANDING_MAP_CORDS)
        ),
        parentFarm,
        isNewOrg: !filteredProperties.length > 0
      };

      // Persist to localStorage for refresh scenarios
      localStorage.setItem(
        PROPERTY_CREATE_STATE,
        JSON.stringify(navigationState)
      );

      history.push(`${paths.properties}/create`, navigationState);
    },
    [amplitude, history]
  );

  const selectProperty = useCallback((property, type) => {
    const payload = { ...property };
    delete payload.$filterRefresh;
    if (type) {
      payload.type = type;
    }
    createAction(dispatch, SELECT_PROPERTY, payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPropertySelection = useCallback(
    selected => {
      let type = 'Crop Zone';
      if ('fields' in selected) {
        type = 'Farm';
        setSelectedField(null);
        fetchFarmCosts(selected.id);
      }

      if ('cropzones' in selected) {
        type = 'Field';
        setSelectedField(selected.id);
        createAction(dispatch, SELECTED_FIELD_ROW_ID, { id: selected.id });
      }

      if (selected?.$parentFieldId) {
        createAction(dispatch, SELECTED_FIELD_ROW_ID, {
          id: selected.$parentFieldId
        });
      }

      selectProperty(selected, type);
      setGeoJSONCollectionFeatures(
        selected?.isFromMap
          ? filterFeatureCollectionByParentIdNoZoom(
              fieldsAndAreasGeoJSONRef.current,
              selected.id
            )
          : filterFeatureCollectionByParentId(
              fieldsAndAreasGeoJSONRef.current,
              selected.id
            )
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatch,
      selectProperty,
      setGeoJSONCollectionFeatures,
      fetchFarmCosts,
      filterFeatureCollectionByParentId
    ]
  );

  const onPropertySelection = useCallback(
    selected => {
      // Re-picking the open property, or passing nothing, clears the selection —
      // which unmounts the edit form, so it needs guarding just like a move to a
      // different property.
      if (!selected || selected?.id === selectedPropertyRef.current?.id) {
        guardNavigation(() => {
          setSelectedField(null);
          selectProperty();
          filterFeatureCollectionByParentId(fieldsAndAreasGeoJSONRef.current);
        });
        return;
      }

      guardNavigation(() => applyPropertySelection(selected));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      guardNavigation,
      applyPropertySelection,
      selectProperty,
      filterFeatureCollectionByParentId
    ]
  );

  useEffect(() => {
    if (
      (loggedInUserOrgPermission?.role === ROLES.FULL_CONTROL ||
        loggedInUserOrgPermission?.role === ROLES.VIEW_ONLY) &&
      !Object.prototype.hasOwnProperty.call(
        loggedInUserOrgPermission,
        'financialAccess'
      )
    ) {
      setFinancialAccess('none');
    } else {
      setFinancialAccess(loggedInUserOrgPermission?.financialAccess);
    }
  }, [loggedInUserOrgPermission]);

  useEffect(() => {
    setParams({ page: 0, textSearch: searchTextValueFFT?.valueInput });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTextValueFFT?.valueInput]);

  useEffect(() => {
    setViewMode(propertyViewStorageApi.get());
  }, [organization]);

  useEffect(() => {
    if (
      shouldResetViewMode({
        selectedProperty,
        currentView: viewMode,
        isCostAnalysisViewEnabled
      })
    ) {
      setViewMode(PROPERTY_VIEW_MODE.MAP);
    }
  }, [selectedProperty, viewMode, isCostAnalysisViewEnabled]);

  useEffect(() => {
    if (!isHugeOrganization) return;
    amplitude.sendEventToAmplitude(
      amplitude.events.epic.Organization.paginatedProperties,
      {
        organizationId: organization?.id
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization.id, isHugeOrganization]);

  const isFarmSelected = selectedProperty?.type === 'Farm';
  const isFieldSelected = selectedProperty?.type === 'Field';
  const isCropZoneSelected = selectedProperty?.type === 'Crop Zone';

  const contextValue = useMemo(
    () => ({
      params,
      setParams,
      pagination,
      // conditions
      isCostAnalysisViewEnabled,
      isPropertyDetailsEnabled,
      isFarmSelected,
      isFieldSelected,
      isCropZoneSelected,
      isLoadingPaginatedProperties,
      isHugeOrganization,

      //
      selectedProperty,
      selectedField,
      resetPropertyFiltersForOrganizationChange,
      financialAccess,
      viewMode,
      onViewModeChange,
      onDrawFieldsClick,

      // unsaved-changes guard
      isFormDirty,
      setFormDirty,
      isNavigationBlocked,
      guardNavigation,
      confirmNavigation,
      cancelNavigation,
      enableEdit,
      setEnableEdit,

      // map
      isLoadingProperties,
      isLoadingFields,
      propertyZoom,
      propertyLandingPageData,
      landingResults,
      showArchivedProperties: propertyFilters?.showArchivedProperties,
      isGlobalCropSeasonLoading,
      rawFieldsAndAreasGeoJSONCollection: unfilteredGeoJSON,
      fieldsAndAreasGeoJSONCollection,

      setGeoJSONCollectionFeatures,
      filterFeatureCollectionByParentId,
      fetchFarmCosts,
      fetchProperties: reloadData,
      updateCropZoneTags,
      onPropertySetZoom,
      onPropertySelection
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      params,
      pagination,
      isCostAnalysisViewEnabled,
      isPropertyDetailsEnabled,
      isFarmSelected,
      isFieldSelected,
      isCropZoneSelected,
      isLoadingPaginatedProperties,
      isHugeOrganization,
      selectedProperty,
      selectedField,
      financialAccess,
      viewMode,
      onPropertySelection,
      isFormDirty,
      isNavigationBlocked,
      guardNavigation,
      confirmNavigation,
      cancelNavigation,
      enableEdit,
      isLoadingProperties,
      isLoadingFields,
      propertyLandingPageData,
      landingResults,
      propertyFilters?.showArchivedProperties,
      resetPropertyFiltersForOrganizationChange,
      isGlobalCropSeasonLoading,
      fieldsAndAreasGeoJSONCollection,
      unfilteredGeoJSON,
      reloadData,
      updateCropZoneTags,
      propertyZoom
    ]
  );

  return (
    <PropertyLandingContext.Provider value={contextValue}>
      {children}
    </PropertyLandingContext.Provider>
  );
}

PropertyLandingProvider.propTypes = {
  children: PropTypes.node.isRequired
};
